import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from '../InterviewIcons';

/**
 * Token-styled listbox replacement for native <select> device pickers.
 * Trigger button + popover listbox; full keyboard support (ArrowUp/Down,
 * Home/End, Enter/Space, Esc), click-outside close, aria-activedescendant
 * tracking, and a drop-up fallback so the popover never leaves the viewport.
 * onChange fires only for a real user pick of a different option.
 */
const DeviceSelect = ({ id, labelId, value, options, onChange, placeholder = 'Select a device' }) => {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [dropUp, setDropUp] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const listRef = useRef(null);

    const selectedIndex = options.findIndex((option) => option.value === value);
    const selectedLabel = selectedIndex >= 0 ? options[selectedIndex].label : placeholder;

    const openList = () => {
        if (options.length === 0) return;
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropUp(spaceBelow < 268 && rect.top > spaceBelow);
        }
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
    };

    const closeList = (refocus) => {
        setOpen(false);
        if (refocus && triggerRef.current) triggerRef.current.focus();
    };

    const commit = (index) => {
        const option = options[index];
        if (option && option.value !== value) onChange(option.value);
        closeList(true);
    };

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (open && listRef.current) listRef.current.focus();
    }, [open]);

    useEffect(() => {
        if (!open || !listRef.current || activeIndex < 0) return;
        const activeEl = listRef.current.children[activeIndex];
        if (activeEl && activeEl.scrollIntoView) activeEl.scrollIntoView({ block: 'nearest' });
    }, [open, activeIndex]);

    const onTriggerKeyDown = (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
            event.preventDefault();
            openList();
        }
    };

    const onListKeyDown = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((i) => Math.min(options.length - 1, i + 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => Math.max(0, i - 1));
        } else if (event.key === 'Home') {
            event.preventDefault();
            setActiveIndex(0);
        } else if (event.key === 'End') {
            event.preventDefault();
            setActiveIndex(options.length - 1);
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            commit(activeIndex);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            closeList(true);
        } else if (event.key === 'Tab') {
            // Refocus the trigger before the list unmounts so the default Tab
            // continues from the picker instead of falling back to <body>
            closeList(true);
        }
    };

    return (
        <div className={`device-select${open ? ' open' : ''}`} ref={rootRef}>
            <button
                type="button"
                id={id}
                ref={triggerRef}
                className="device-select-trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-labelledby={labelId ? `${labelId} ${id}` : undefined}
                onClick={() => (open ? closeList(true) : openList())}
                onKeyDown={onTriggerKeyDown}
            >
                <span className="device-select-value">{selectedLabel}</span>
                <ChevronDown size={14} className="device-select-chevron" />
            </button>
            {open && (
                <ul
                    ref={listRef}
                    className={`device-select-list${dropUp ? ' drop-up' : ''}`}
                    role="listbox"
                    tabIndex={-1}
                    aria-labelledby={labelId}
                    aria-activedescendant={activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
                    onKeyDown={onListKeyDown}
                >
                    {options.map((option, index) => (
                        <li
                            key={option.value || `option-${index}`}
                            id={`${id}-option-${index}`}
                            role="option"
                            aria-selected={option.value === value}
                            className={`device-select-option${index === activeIndex ? ' active' : ''}`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onClick={() => commit(index)}
                        >
                            <span className="device-select-check" aria-hidden="true">
                                {option.value === value && <Check size={14} />}
                            </span>
                            <span className="device-select-option-label">{option.label}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default DeviceSelect;
